import {
  AppParametersService,
  BaseAppParameters,
  BaseEnvironmentConfig,
  ENV,
  RealEstateObject,
  SupportedLanguage,
} from '@immosky/types';
import { BehaviorSubject, Observable } from 'rxjs';
import { Inject, Injectable } from '@angular/core';
import { getClosestSupportedLanguage } from './i18n.utils';
import { MessageTransformer } from './message-transformer';
import { overrideTranslations, translations } from './translations';
import { ReplacementArgs } from './replacement-args';
import { formatNumber } from '../utils';

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  private messageTransformer = new MessageTransformer();
  private languageChange$ = new BehaviorSubject<SupportedLanguage>(
    this.environment.language
  );

  currentLanguage = this.environment.language;

  get currentLangCode(): SupportedLanguage {
    return this.currentLanguage.substr(0, 2) as SupportedLanguage;
  }

  get languageChange(): Observable<SupportedLanguage> {
    return this.languageChange$.asObservable();
  }

  constructor(
    @Inject(ENV) private environment: BaseEnvironmentConfig,
    private appParametersService: AppParametersService<BaseAppParameters>
  ) {
    this.appParametersService.parameterChanges$.subscribe(() => {
      this.setLanguage(this.appParametersService.get().language);
    });

    // add setLanguage function to window, so you can change language via console
    // @ts-ignore
    window.setLanguage = (language: SupportedLanguage) => {
      this.setLanguage(language);
    };
  }

  setLanguage(language: SupportedLanguage): void {
    if (language) {
      this.currentLanguage = getClosestSupportedLanguage(language);
      this.languageChange$.next(this.currentLanguage);
      const country = this.appParametersService.get().countries[0];

      let translationKey = `${this.currentLanguage}_${country}`;
      if (this.appParametersService.get().branding != 'immosky') {
        translationKey = `${this.currentLanguage}_${this.appParametersService.get().branding}`;
      } 

      this.messageTransformer.setTranslation(
        translations[this.currentLanguage],
        overrideTranslations[translationKey] || {}
      );
    }
  }

  transform(messagePath: string, replacements?: ReplacementArgs): string {
    return this.messageTransformer.transform(messagePath, replacements);
  }

  renderPrice(object: RealEstateObject): string {
    const contractType = object.contractType;
    const price = object.price;

    if (contractType === 'buy' && price.total) {
      return this.messageTransformer.transform('common.currency.price', {
        price: this.formatNumber(price.total),
      });
    } else if (contractType === 'rent' && price.rentGross) {
      return this.messageTransformer.transform(
        'common.currency.pricePerMonth',
        {
          price: this.formatNumber(price.rentGross),
        }
      );
    }

    return '';
  }

  formatNumber(value: number): string {
    return formatNumber(value, this.appParametersService.get().countries[0]);
  }
}
